import React, { useEffect, useState } from 'react';
import { apiFetch } from '../App';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Database, Download, Upload, Loader2, Package, Film, Server, Trash2, ShieldAlert, History } from 'lucide-react';

/*
 * AdminDbMigrateTab
 * ─────────────────
 * Data-migration panel used to clone the 7 supported collections from
 * one WIM environment (Emergent preview) to another (self-hosted server).
 *
 * Pattern extends the existing coach_clips export/import to the seven
 * collections listed in DB_MIGRATE_COLLECTIONS on the backend. Each row
 * carries its natural key so re-imports upsert cleanly (idempotent).
 *
 * ALL actions require admin auth (the backend enforces this).
 */
const AdminDbMigrateTab = () => {
  const [loading, setLoading] = useState(false);
  const [collections, setCollections] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [bundleBusy, setBundleBusy] = useState(false);

  // Media manifest state — loaded lazily when the admin opens the section.
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaSummary, setMediaSummary] = useState(null);
  const [includeThirdParty, setIncludeThirdParty] = useState(false);

  // Base64 cleanup state
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupExecuting, setCleanupExecuting] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [cleanupHistory, setCleanupHistory] = useState([]);
  const [cleanupHistoryLoading, setCleanupHistoryLoading] = useState(false);
  const [rescueBusy, setRescueBusy] = useState(false);
  const [rescueExecuting, setRescueExecuting] = useState(false);
  const [rescueResult, setRescueResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiFetch('/admin/db-migrate/collections');
      setCollections(data?.collections || []);
    } catch (err) {
      toast.error(`Could not load collections: ${err?.message || 'unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ── Media manifest ─────────────────────────────────────────────────
  const loadMediaSummary = async (thirdParty) => {
    setMediaLoading(true);
    try {
      const url = `/admin/db-migrate/media-manifest?include_third_party=${thirdParty ? 'true' : 'false'}`;
      const { data } = await apiFetch(url);
      // Keep the file list out of state — it can be huge. Only preview
      // metadata + the first 5 rows.
      setMediaSummary({
        generated_at: data.generated_at,
        include_third_party: data.include_third_party,
        host_groups: data.host_groups,
        host_totals: data.host_totals,
        totals: data.totals,
        sample_files: (data.files || []).slice(0, 5),
      });
    } catch (err) {
      toast.error(`Media scan failed: ${err?.message || 'unknown error'}`);
    } finally {
      setMediaLoading(false);
    }
  };

  const downloadRaw = async (url, filename, mimeType = 'application/json') => {
    try {
      const { data } = await apiFetch(url);
      const text = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
      const blob = new Blob([text], { type: mimeType });
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      toast.error(`Download failed: ${err?.message || 'unknown error'}`);
    }
  };

  const downloadManifest = () => {
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    return downloadRaw(
      `/admin/db-migrate/media-manifest?include_third_party=${includeThirdParty ? 'true' : 'false'}`,
      `wim_media_manifest_${stamp}.json`,
    );
  };

  const downloadScript = (fmt) => {
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    return downloadRaw(
      `/admin/db-migrate/media-download-script?fmt=${fmt}&include_third_party=${includeThirdParty ? 'true' : 'false'}`,
      `wim_media_mirror_${fmt}_${stamp}.sh`,
      'text/x-shellscript',
    );
  };

  // ── Downloads ──────────────────────────────────────────────────────
  const downloadJson = async (url, filename) => {
    try {
      // apiFetch parses JSON eagerly; re-serialize for the download blob so
      // we still get a native file download UX (Content-Disposition works
      // too, but only when using the raw browser navigation — Blob path
      // keeps the auth cookie flow uniform).
      const { data } = await apiFetch(url);
      const blob = new Blob(
        [typeof data === 'string' ? data : JSON.stringify(data, null, 2)],
        { type: 'application/json' },
      );
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      toast.error(`Export failed: ${err?.message || 'unknown error'}`);
    }
  };

  const exportOne = (name) => {
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    return downloadJson(`/admin/db-migrate/export/${name}`, `wim_${name}_${stamp}.json`);
  };
  const exportBundle = async () => {
    setBundleBusy(true);
    try {
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      await downloadJson('/admin/db-migrate/export-all', `wim_db_bundle_${stamp}.json`);
    } finally { setBundleBusy(false); }
  };

  // ── Imports ────────────────────────────────────────────────────────
  const importFile = async (file, collectionName /* null for bundle */) => {
    if (!file) return;
    const text = await file.text();
    let payload;
    try { payload = JSON.parse(text); }
    catch { toast.error('That file is not valid JSON'); return; }
    setImportResult(null);
    try {
      const isBundle = payload?.kind === 'wim_db_bundle';
      const url = isBundle
        ? '/admin/db-migrate/import-bundle'
        : `/admin/db-migrate/import/${collectionName}`;
      const { data } = await apiFetch(url, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setImportResult({ scope: isBundle ? 'bundle' : collectionName, result: data });
      toast.success('Import complete');
      load(); // refresh counts
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'unknown error';
      toast.error(`Import failed: ${detail}`);
    }
  };

  // ── Base64 cleanup ─────────────────────────────────────────────────
  const loadCleanupHistory = async () => {
    setCleanupHistoryLoading(true);
    try {
      const { data } = await apiFetch('/admin/db-cleanup/history?limit=20');
      setCleanupHistory(data?.history || []);
    } catch (err) {
      toast.error(`Could not load cleanup history: ${err?.message || 'unknown error'}`);
    } finally {
      setCleanupHistoryLoading(false);
    }
  };

  useEffect(() => { loadCleanupHistory(); }, []);

  const runCleanup = async (dryRun) => {
    if (!dryRun) {
      const ok = window.confirm(
        'Execute base64 cleanup?\n\n' +
        'This will $unset redundant base64 image fields from documents where a valid ' +
        'https:// companion URL already exists. Safe by construction (never touches rows ' +
        'without a companion URL), but the write is permanent.\n\nProceed?'
      );
      if (!ok) return;
    }

    if (dryRun) setCleanupBusy(true); else setCleanupExecuting(true);
    try {
      const { data } = await apiFetch(
        `/admin/db-cleanup/inline-base64?dry_run=${dryRun ? 'true' : 'false'}`,
        { method: 'POST' },
      );
      setCleanupResult(data);
      if (dryRun) {
        toast.success(`Dry run: ${data.total_docs_affected} doc(s), ~${data.total_mb_reclaimed} MB to reclaim`);
      } else {
        toast.success(`Cleanup done — ${data.total_docs_affected} doc(s) updated, ${data.total_mb_reclaimed} MB reclaimed`);
        loadCleanupHistory();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'unknown error';
      toast.error(`Cleanup failed: ${detail}`);
    } finally {
      if (dryRun) setCleanupBusy(false); else setCleanupExecuting(false);
    }
  };

  const runRescue = async (dryRun) => {
    if (!dryRun) {
      const ok = window.confirm(
        'Rescue orphan base64 images?\n\n' +
        'For every album_covers doc that has base64 data but NO valid https:// URL, ' +
        "this will decode the image, upload it to HP media, and $set the doc's image_url " +
        'to the new HP URL. Base64 fields are NOT stripped here — run the regular ' +
        'cleanup after rescue to strip them.\n\nProceed?'
      );
      if (!ok) return;
    }

    if (dryRun) setRescueBusy(true); else setRescueExecuting(true);
    try {
      const { data } = await apiFetch(
        `/admin/db-cleanup/rescue-orphan-base64?dry_run=${dryRun ? 'true' : 'false'}`,
        { method: 'POST' },
      );
      setRescueResult(data);
      if (dryRun) {
        const total = (data.plan || []).reduce((s, p) => s + (p.orphan_docs || 0), 0);
        toast.success(`Dry run: ${total} orphan(s) found, ~${data.total_mb_uploaded} MB would upload`);
      } else {
        toast.success(`Rescue done — ${data.total_docs_rescued} doc(s) rescued, ${data.total_mb_uploaded} MB uploaded${data.errors_count ? `, ${data.errors_count} error(s)` : ''}`);
        loadCleanupHistory();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'unknown error';
      toast.error(`Rescue failed: ${detail}`);
    } finally {
      if (dryRun) setRescueBusy(false); else setRescueExecuting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-db-migrate-tab">
      <div className="rounded-lg bg-white/5 border border-white/10 p-5">
        <div className="flex items-center gap-3 mb-1">
          <Database className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold">Database migrate — export / import</h2>
        </div>
        <p className="text-sm text-zinc-400">
          Clone core collections between environments (Emergent preview ↔ self-hosted server).
          Each row upserts on its natural key, so re-importing is safe and idempotent.
          Exports include sensitive data — treat downloads like a Mongo dump.
        </p>
      </div>

      {/* Full bundle */}
      <div className="rounded-lg bg-white/5 border border-white/10 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Package className="h-4 w-4 text-primary" />
          <h3 className="font-semibold">Full bundle</h3>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          Downloads every supported collection as a single JSON file. Importing the
          same file elsewhere upserts users first, then songs, campaigns, codes,
          batches, clips and style presets in order.
        </p>
        <div className="flex flex-wrap gap-3 items-center">
          <Button
            onClick={exportBundle}
            disabled={bundleBusy}
            data-testid="db-migrate-export-bundle-btn"
            className="flex items-center gap-2"
          >
            {bundleBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Download bundle (.json)
          </Button>
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white/10 hover:bg-white/20 cursor-pointer text-sm" data-testid="db-migrate-import-bundle-label">
            <Upload className="h-4 w-4" />
            Import bundle…
            <input
              type="file"
              accept="application/json,.json"
              className="hidden"
              data-testid="db-migrate-import-bundle-input"
              onChange={(e) => importFile(e.target.files?.[0], null)}
            />
          </label>
        </div>
      </div>

      {/* Per-collection controls */}
      <div className="rounded-lg bg-white/5 border border-white/10 p-5">
        <h3 className="font-semibold mb-3">Per collection</h3>
        {loading ? (
          <div className="flex items-center gap-2 text-zinc-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading counts…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {collections.map((c) => (
              <div
                key={c.collection}
                className="flex items-center justify-between p-3 rounded-md bg-black/30 border border-white/5"
                data-testid={`db-migrate-row-${c.collection}`}
              >
                <div>
                  <div className="font-medium">{c.label}</div>
                  <div className="text-xs text-zinc-500">
                    {c.count.toLocaleString()} rows &middot; key <code>{c.key}</code>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => exportOne(c.collection)}
                    data-testid={`db-migrate-export-${c.collection}`}
                  >
                    <Download className="h-3.5 w-3.5 mr-1" /> Export
                  </Button>
                  <label
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-white/10 hover:bg-white/20 cursor-pointer text-xs"
                    data-testid={`db-migrate-import-${c.collection}-label`}
                  >
                    <Upload className="h-3.5 w-3.5" /> Import
                    <input
                      type="file"
                      accept="application/json,.json"
                      className="hidden"
                      data-testid={`db-migrate-import-${c.collection}-input`}
                      onChange={(e) => importFile(e.target.files?.[0], c.collection)}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Base64 cleanup */}
      <div
        className="rounded-lg bg-white/5 border border-white/10 p-5"
        data-testid="db-migrate-cleanup-card"
      >
        <div className="flex items-center gap-2 mb-3">
          <Trash2 className="h-4 w-4 text-primary" />
          <h3 className="font-semibold">Inline base64 cleanup</h3>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          Strips redundant inline <code>base64</code> copies of images from documents where a
          valid <code>https://</code> companion URL already exists on HP media. Safe by
          construction — never touches a row whose companion URL is missing or still a data URI.
          Always dry-run first to preview the impact.
        </p>

        <div className="flex flex-wrap gap-3 items-center mb-4">
          <Button
            variant="outline"
            onClick={() => runCleanup(true)}
            disabled={cleanupBusy || cleanupExecuting}
            data-testid="db-cleanup-dry-run-btn"
            className="flex items-center gap-2"
          >
            {cleanupBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldAlert className="h-4 w-4" />}
            Dry run
          </Button>
          <Button
            onClick={() => runCleanup(false)}
            disabled={cleanupBusy || cleanupExecuting}
            data-testid="db-cleanup-execute-btn"
            className="flex items-center gap-2 bg-rose-600 hover:bg-rose-500"
          >
            {cleanupExecuting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Execute cleanup
          </Button>
          <div className="w-px h-6 bg-white/10 mx-1" />
          <Button
            variant="outline"
            onClick={() => runRescue(true)}
            disabled={rescueBusy || rescueExecuting}
            data-testid="db-rescue-dry-run-btn"
            className="flex items-center gap-2"
          >
            {rescueBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldAlert className="h-4 w-4" />}
            Rescue orphans (dry run)
          </Button>
          <Button
            onClick={() => runRescue(false)}
            disabled={rescueBusy || rescueExecuting}
            data-testid="db-rescue-execute-btn"
            className="flex items-center gap-2 bg-amber-600 hover:bg-amber-500"
          >
            {rescueExecuting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Rescue orphans
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadCleanupHistory}
            disabled={cleanupHistoryLoading}
            data-testid="db-cleanup-refresh-history-btn"
            className="flex items-center gap-2 text-zinc-300"
          >
            {cleanupHistoryLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <History className="h-3.5 w-3.5" />}
            Refresh history
          </Button>
        </div>

        {rescueResult && (
          <div
            className={`rounded-md p-4 mb-4 border ${rescueResult.dry_run
              ? 'bg-amber-500/10 border-amber-500/30'
              : 'bg-blue-500/10 border-blue-500/30'}`}
            data-testid="db-rescue-result"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium">
                {rescueResult.dry_run ? 'Rescue dry run (nothing was uploaded)' : 'Rescue executed'}
              </div>
              <div className="text-xs text-zinc-400">
                {rescueResult.ran_at ? new Date(rescueResult.ran_at).toLocaleString() : '—'}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <div className="rounded bg-black/30 p-3">
                <div className="text-xs uppercase tracking-wide text-zinc-400">Docs rescued</div>
                <div className="text-2xl font-semibold" data-testid="db-rescue-docs">
                  {rescueResult.total_docs_rescued?.toLocaleString?.() ?? rescueResult.total_docs_rescued ?? 0}
                </div>
              </div>
              <div className="rounded bg-black/30 p-3">
                <div className="text-xs uppercase tracking-wide text-zinc-400">MB uploaded</div>
                <div className="text-2xl font-semibold" data-testid="db-rescue-mb">
                  {rescueResult.total_mb_uploaded} MB
                </div>
              </div>
              <div className="rounded bg-black/30 p-3">
                <div className="text-xs uppercase tracking-wide text-zinc-400">Errors</div>
                <div className={`text-2xl font-semibold ${rescueResult.errors_count ? 'text-rose-400' : ''}`} data-testid="db-rescue-errors">
                  {rescueResult.errors_count ?? 0}
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-zinc-400">
                  <tr>
                    <th className="text-left py-1 pr-4">Collection</th>
                    <th className="text-left py-1 pr-4">Field</th>
                    <th className="text-right py-1 pr-4">Orphans</th>
                    <th className="text-right py-1 pr-4">Uploaded</th>
                    <th className="text-right py-1">Bytes</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-200">
                  {(rescueResult.plan || []).map((p, i) => (
                    <tr key={`rescue-${p.collection}-${p.field}-${i}`} className="border-t border-white/5">
                      <td className="py-1 pr-4"><code>{p.collection}</code></td>
                      <td className="py-1 pr-4"><code>{p.field}</code></td>
                      <td className="py-1 pr-4 text-right">{p.orphan_docs}</td>
                      <td className="py-1 pr-4 text-right">{p.uploaded ?? 0}</td>
                      <td className="py-1 text-right">{p.bytes_would_upload?.toLocaleString?.() ?? p.bytes_would_upload}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {cleanupResult && (
          <div
            className={`rounded-md p-4 mb-4 border ${cleanupResult.dry_run
              ? 'bg-amber-500/10 border-amber-500/30'
              : 'bg-emerald-500/10 border-emerald-500/30'}`}
            data-testid="db-cleanup-result"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium">
                {cleanupResult.dry_run ? 'Dry run summary (nothing was written)' : 'Cleanup executed'}
              </div>
              <div className="text-xs text-zinc-400">
                {cleanupResult.ran_at ? new Date(cleanupResult.ran_at).toLocaleString() : '—'}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <div className="rounded bg-black/30 p-3">
                <div className="text-xs uppercase tracking-wide text-zinc-400">Docs affected</div>
                <div className="text-2xl font-semibold" data-testid="db-cleanup-docs-affected">
                  {cleanupResult.total_docs_affected?.toLocaleString?.() ?? cleanupResult.total_docs_affected}
                </div>
              </div>
              <div className="rounded bg-black/30 p-3">
                <div className="text-xs uppercase tracking-wide text-zinc-400">Bytes reclaimed</div>
                <div className="text-2xl font-semibold">
                  {cleanupResult.total_bytes_reclaimed?.toLocaleString?.() ?? cleanupResult.total_bytes_reclaimed}
                </div>
              </div>
              <div className="rounded bg-black/30 p-3">
                <div className="text-xs uppercase tracking-wide text-zinc-400">Megabytes</div>
                <div className="text-2xl font-semibold" data-testid="db-cleanup-mb-reclaimed">
                  {cleanupResult.total_mb_reclaimed} MB
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-zinc-400">
                  <tr>
                    <th className="text-left py-1 pr-4">Collection</th>
                    <th className="text-left py-1 pr-4">Field</th>
                    <th className="text-left py-1 pr-4">Companion</th>
                    <th className="text-right py-1 pr-4">Docs</th>
                    <th className="text-right py-1 pr-4">Bytes</th>
                    <th className="text-right py-1">Skipped</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-200">
                  {(cleanupResult.plan || []).map((p, i) => (
                    <tr key={`${p.collection}-${p.field}-${i}`} className="border-t border-white/5">
                      <td className="py-1 pr-4"><code>{p.collection}</code></td>
                      <td className="py-1 pr-4"><code>{p.field}</code></td>
                      <td className="py-1 pr-4"><code>{p.companion_url_field}</code></td>
                      <td className="py-1 pr-4 text-right">{p.candidate_docs?.toLocaleString?.() ?? p.candidate_docs}</td>
                      <td className="py-1 pr-4 text-right">{p.bytes_would_reclaim?.toLocaleString?.() ?? p.bytes_would_reclaim}</td>
                      <td className="py-1 text-right text-zinc-500">{p.skipped_no_companion_url}</td>
                    </tr>
                  ))}
                  {(cleanupResult.plan || []).length === 0 && (
                    <tr><td colSpan={6} className="py-2 text-zinc-500 italic">No targets in plan.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-2">
          <div className="text-xs uppercase tracking-wide text-zinc-400 mb-2 flex items-center gap-2">
            <History className="h-3 w-3" /> Recent runs
          </div>
          {cleanupHistoryLoading ? (
            <div className="flex items-center gap-2 text-zinc-500 text-sm">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading history…
            </div>
          ) : cleanupHistory.length === 0 ? (
            <div className="text-sm text-zinc-500 italic" data-testid="db-cleanup-history-empty">
              No cleanup runs recorded yet. Dry runs don&apos;t count — only real executions are logged.
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="db-cleanup-history-list">
              <table className="w-full text-xs">
                <thead className="text-zinc-400">
                  <tr>
                    <th className="text-left py-1 pr-4">When</th>
                    <th className="text-left py-1 pr-4">By</th>
                    <th className="text-left py-1 pr-4">Kind</th>
                    <th className="text-right py-1 pr-4">Docs</th>
                    <th className="text-right py-1">MB</th>
                  </tr>
                </thead>
                <tbody className="text-zinc-200">
                  {cleanupHistory.map((h) => (
                    <tr
                      key={h.cleanup_id}
                      className="border-t border-white/5"
                      data-testid={`db-cleanup-history-row-${h.cleanup_id}`}
                    >
                      <td className="py-1 pr-4 whitespace-nowrap">
                        {h.ran_at ? new Date(h.ran_at).toLocaleString() : '—'}
                      </td>
                      <td className="py-1 pr-4"><code className="text-zinc-300">{h.run_by || '—'}</code></td>
                      <td className="py-1 pr-4"><code className="text-zinc-400">{h.kind || '—'}</code></td>
                      <td className="py-1 pr-4 text-right">{h.total_docs_affected?.toLocaleString?.() ?? h.total_docs_affected}</td>
                      <td className="py-1 text-right">{h.total_mb_reclaimed} MB</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Media manifest */}
      <div className="rounded-lg bg-white/5 border border-white/10 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Film className="h-4 w-4 text-primary" />
          <h3 className="font-semibold">Media manifest — mirror uploaded assets</h3>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          Scans <code>songs</code>, <code>style_presets</code>, <code>animation_clips</code>, and
          <code>coach_clips</code> for external media URLs (audio, video, images).
          Downloads a JSON manifest of every unique URL plus a shell script that mirrors
          them into a local directory, ready to <code>rsync</code> to the self-hosted media root.
        </p>

        <label
          className="inline-flex items-center gap-2 mb-4 text-sm text-zinc-300"
          data-testid="db-migrate-media-third-party-toggle"
        >
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={includeThirdParty}
            onChange={(e) => setIncludeThirdParty(e.target.checked)}
          />
          Include third-party CDNs (Suno / MusicGPT temp URLs — may 404)
        </label>

        <div className="flex flex-wrap gap-3 items-center mb-4">
          <Button
            variant="outline"
            onClick={() => loadMediaSummary(includeThirdParty)}
            disabled={mediaLoading}
            data-testid="db-migrate-media-scan-btn"
            className="flex items-center gap-2"
          >
            {mediaLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Server className="h-4 w-4" />}
            Scan &amp; preview
          </Button>
          <Button
            onClick={downloadManifest}
            data-testid="db-migrate-media-manifest-btn"
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Download manifest.json
          </Button>
          <Button
            variant="secondary"
            onClick={() => downloadScript('curl')}
            data-testid="db-migrate-media-script-curl-btn"
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Mirror script (curl)
          </Button>
          <Button
            variant="secondary"
            onClick={() => downloadScript('wget')}
            data-testid="db-migrate-media-script-wget-btn"
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Mirror script (wget)
          </Button>
        </div>

        {mediaSummary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="db-migrate-media-summary">
            {['controlled', 'emergent', 'third_party'].map((group) => {
              const hosts = mediaSummary.host_groups?.[group] || [];
              const groupTotal = hosts.reduce((s, h) => s + (mediaSummary.host_totals?.[h] || 0), 0);
              const label = group === 'controlled' ? 'Yours to mirror'
                : group === 'emergent' ? 'Emergent CDN (must mirror before cutover)'
                : 'Third-party (may already be dead)';
              const tone = group === 'controlled' ? 'bg-emerald-500/10 border-emerald-500/30'
                : group === 'emergent' ? 'bg-amber-500/10 border-amber-500/30'
                : 'bg-zinc-500/10 border-zinc-500/30';
              return (
                <div key={group} className={`rounded-md p-3 border ${tone}`}>
                  <div className="text-xs uppercase tracking-wide text-zinc-400 mb-1">{label}</div>
                  <div className="text-2xl font-semibold" data-testid={`db-migrate-media-total-${group}`}>{groupTotal}</div>
                  <ul className="mt-2 text-xs text-zinc-300 space-y-1">
                    {hosts.map((h) => (
                      <li key={h} className="flex justify-between">
                        <code>{h}</code>
                        <span className="text-zinc-500">{mediaSummary.host_totals?.[h] || 0}</span>
                      </li>
                    ))}
                    {hosts.length === 0 && <li className="text-zinc-500">no hosts</li>}
                  </ul>
                </div>
              );
            })}
            <div className="md:col-span-3 text-xs text-zinc-500 mt-1">
              Unique URLs: <strong>{mediaSummary.totals?.unique_urls || 0}</strong> &middot;
              Scanned at {mediaSummary.generated_at ? new Date(mediaSummary.generated_at).toLocaleString() : '—'}
              {mediaSummary.sample_files?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer">Sample files</summary>
                  <pre className="mt-2 text-[10px] whitespace-pre-wrap">{JSON.stringify(mediaSummary.sample_files, null, 2)}</pre>
                </details>
              )}
            </div>
          </div>
        )}
      </div>

      {importResult && (
        <div
          className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-4 text-sm"
          data-testid="db-migrate-last-result"
        >
          <div className="font-medium mb-1">Last import — {importResult.scope}</div>
          <pre className="text-xs whitespace-pre-wrap text-emerald-100">{JSON.stringify(importResult.result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

export default AdminDbMigrateTab;
